import {
    type AgActiveItemState,
    type AgRadialGaugeMarkerShape,
    type AgRadialGaugeOptions,
    type AgRadialGaugeTargetPlacement,
    type AgSeriesMarkerStyle,
    type FontStyle,
    type FontWeight,
    type TextAlign,
    type VerticalAlign,
    _ModuleSupport,
} from 'ag-charts-community';
import type { FillStrokeMorph, Normalised } from 'ag-charts-core';
import {
    type ChartAnimationPhase,
    type DynamicContext,
    type NormalisedTextOrSegments,
    type Point,
    StateMachine,
    createBigIntTicks,
    isBetweenAngles,
    isNumberEqual,
    mergeDefaults,
    normalizeAngle360,
    normalizeAngle360Inclusive,
    tickFormat,
    toPlainText,
    toRadians,
} from 'ag-charts-core';
import type { AgNumericValue, AgRadialGaugeSeriesStyle } from 'ag-charts-types';

import { LinearAngleScale } from '../../axes/angle-number/linearAngleScale';
import { formatWithContext } from '../../utils/formatter';
import { DatumUnion } from '../gauge-util/datumUnion';
import { getGaugeTooltipInfo } from '../gauge-util/gaugeTooltip';
import { fadeInFns, formatLabel, getLabelText } from '../gauge-util/label';
import { LineMarker, lineMarker } from '../gauge-util/lineMarker';
import { findGaugeNodeDatum, pickGaugeFocus, pickGaugeNearestDatum } from '../gauge-util/pick';
import { RadialGaugeNeedle } from './radialGaugeNeedle';
import {
    LabelType,
    NodeDataType,
    type RadialGaugeLabelDatum,
    type RadialGaugeNodeDatum,
    RadialGaugeSeriesProperties,
    type RadialGaugeTargetDatum,
    type RadialGaugeTargetDatumLabel,
} from './radialGaugeSeriesProperties';
import {
    formatRadialGaugeLabels,
    prepareRadialGaugeSeriesAnimationFunctions,
    resetRadialGaugeSeriesResetNeedleFunction,
    resetRadialGaugeSeriesResetSectorFunction,
} from './radialGaugeUtil';

const {
    fromToMotion,
    resetMotion,
    SeriesNodePickMode,
    createDatumId,
    sectorBox,
    BBox,
    Group,
    PointerEvents,
    Selection,
    Sector,
    SectorBox,
    Transformable,
    TransformableText,
    Text,
} = _ModuleSupport;

type SeriesNodeDatum = _ModuleSupport.SeriesNodeDatum;

type NormalisedRadialGaugeSeriesStyle = Normalised<AgRadialGaugeSeriesStyle, never, FillStrokeMorph>;

interface TargetLabel {
    enabled: boolean;
    color: string;
    fontStyle: FontStyle;
    fontWeight: FontWeight;
    fontSize: number;
    fontFamily: string;
    spacing: number;
}

interface Target {
    text: string | undefined;
    value: number;
    shape: AgRadialGaugeMarkerShape;
    placement: AgRadialGaugeTargetPlacement;
    spacing: number;
    size: number;
    rotation: number;
    label: TargetLabel;
    style: AgSeriesMarkerStyle;
}

type GaugeAnimationState = 'empty' | 'ready' | 'waiting' | 'clearing';
type GaugeAnimationEvent = {
    update: undefined;
    updateData: undefined;
    highlight: undefined;
    highlightMarkers: undefined;
    resize: undefined;
    clear: undefined;
    reset: undefined;
    skip: undefined;
};

interface RadialGaugeNeedleDatum {
    centerX: number;
    centerY: number;
    radius: number;
    angle: number;
    series: RadialGaugeSeries;
}

interface RadialGaugeTickDatum {
    index: number;
    value: AgNumericValue;
    text: NormalisedTextOrSegments;
}

interface RadialGaugeNodeDataContext extends _ModuleSupport.SeriesNodeDataContext<
    RadialGaugeNodeDatum,
    RadialGaugeLabelDatum
> {
    needleData: RadialGaugeNeedleDatum[];
    targetData: RadialGaugeTargetDatum[];
    scaleData: RadialGaugeNodeDatum[];
    tickData: RadialGaugeTickDatum[];
}

const targetPlacementRotation: Record<AgRadialGaugeTargetPlacement, number> = {
    inside: 90,
    middle: 0,
    outside: -90,
};
const outsideLabelPlacements: Array<{ textAlign: CanvasTextAlign; textBaseline: CanvasTextBaseline }> = [
    { textAlign: 'left', textBaseline: 'top' },
    { textAlign: 'right', textBaseline: 'top' },
    { textAlign: 'right', textBaseline: 'bottom' },
    { textAlign: 'left', textBaseline: 'bottom' },
];
const insideLabelPlacements: Array<{ textAlign: CanvasTextAlign; textBaseline: CanvasTextBaseline }> = [
    { textAlign: 'right', textBaseline: 'bottom' },
    { textAlign: 'left', textBaseline: 'bottom' },
    { textAlign: 'left', textBaseline: 'top' },
    { textAlign: 'right', textBaseline: 'top' },
];

export class RadialGaugeSeries
    extends _ModuleSupport.Series<
        RadialGaugeNodeDatum,
        AgRadialGaugeOptions,
        RadialGaugeSeriesProperties,
        RadialGaugeLabelDatum,
        RadialGaugeNodeDataContext
    >
    implements _ModuleSupport.GaugeSeries
{
    static override readonly className = 'RadialGaugeSeries';
    static readonly type = 'radial-gauge' as const;

    public centerX: number = 0;
    public centerY: number = 0;
    public radius: number = 0;
    public textAlign: TextAlign = 'center';
    public verticalAlign: VerticalAlign = 'middle';
    override properties = new RadialGaugeSeriesProperties();

    public scale = new LinearAngleScale();

    private readonly scaleGroup = this.contentGroup.appendChild(new Group({ name: 'scaleGroup' }));
    private readonly itemGroup = this.contentGroup.appendChild(new Group({ name: 'itemGroup' }));
    private readonly itemNeedleGroup = this.contentGroup.appendChild(new Group({ name: 'itemNeedleGroup' }));
    private readonly itemTargetGroup = this.contentGroup.appendChild(new Group({ name: 'itemTargetGroup' }));
    private readonly itemTargetLabelGroup = this.contentGroup.appendChild(new Group({ name: 'itemTargetLabelGroup' }));
    private readonly itemLabelGroup = this.contentGroup.appendChild(new Group({ name: 'itemLabelGroup' }));
    private readonly highlightTargetGroup = this.highlightGroup.appendChild(
        new Group({ name: 'itemTargetLabelGroup' })
    );
    private readonly tickGroup = this.contentGroup.appendChild(new Group({ name: 'tickGroup' }));

    private scaleSelection = Selection.select<_ModuleSupport.Sector<RadialGaugeNodeDatum>>(this.scaleGroup, () =>
        this.nodeFactory()
    );
    private datumSelection = Selection.select<_ModuleSupport.Sector<RadialGaugeNodeDatum>>(this.itemGroup, () =>
        this.nodeFactory()
    );
    private needleSelection = Selection.selectNoInference<RadialGaugeNeedleDatum, RadialGaugeNeedle>(
        this.itemNeedleGroup,
        RadialGaugeNeedle
    );
    public targetSelection = Selection.select<_ModuleSupport.Marker<RadialGaugeTargetDatum>>(this.itemTargetGroup, () =>
        this.markerFactory()
    );
    private targetLabelSelection = Selection.select<_ModuleSupport.Text<RadialGaugeTargetDatum>>(
        this.itemTargetLabelGroup,
        Text<RadialGaugeTargetDatum>
    );
    private labelSelection = Selection.select<_ModuleSupport.Text<RadialGaugeLabelDatum>>(
        this.itemLabelGroup,
        Text<RadialGaugeLabelDatum>
    );
    private highlightTargetSelection = Selection.select<_ModuleSupport.Marker<RadialGaugeTargetDatum>>(
        this.highlightTargetGroup,
        () => this.markerFactory()
    );
    private tickSelection = Selection.select<_ModuleSupport.TransformableText<RadialGaugeTickDatum>>(
        this.tickGroup,
        _ModuleSupport.TransformableText
    );

    public datumUnion: DatumUnion<_ModuleSupport.Sector<RadialGaugeNodeDatum>> = new DatumUnion();
    private readonly animationState: StateMachine<GaugeAnimationState, GaugeAnimationEvent>;

    public contextNodeData?: RadialGaugeNodeDataContext;

    constructor(moduleCtx: DynamicContext<_ModuleSupport.ChartRegistry>) {
        super({
            moduleCtx,
            pickModes: [SeriesNodePickMode.EXACT_SHAPE_MATCH, SeriesNodePickMode.NEAREST_NODE],
        });

        this.animationState = new StateMachine<GaugeAnimationState, GaugeAnimationEvent>('empty', {
            empty: {
                update: {
                    target: 'ready',
                    action: () => this.animateEmptyUpdateReady(),
                },
                reset: 'empty',
                skip: 'ready',
            },
            ready: {
                updateData: 'waiting',
                clear: 'clearing',
                resize: () => this.animateReadyResize(),
                reset: 'empty',
                skip: 'ready',
            },
            waiting: {
                update: {
                    target: 'ready',
                    action: () => this.animateWaitingUpdateReady(),
                },
                reset: 'empty',
                skip: 'ready',
            },
            clearing: {
                update: {
                    target: 'empty',
                },
                reset: 'empty',
                skip: 'ready',
            },
        });

        this.scaleGroup.pointerEvents = PointerEvents.None;
        this.tickGroup.pointerEvents = PointerEvents.None;
        this.itemNeedleGroup.pointerEvents = PointerEvents.None;
        this.itemLabelGroup.pointerEvents = PointerEvents.None;
    }

    override get hasData(): boolean {
        return this.properties.value != null;
    }

    private nodeFactory(): _ModuleSupport.Sector<RadialGaugeNodeDatum> {
        return new Sector<RadialGaugeNodeDatum>();
    }

    private markerFactory(): _ModuleSupport.Marker<RadialGaugeTargetDatum> {
        const marker = new LineMarker<RadialGaugeTargetDatum>();
        marker.size = 1;
        return marker;
    }

    override processData() {
        this.nodeDataRefresh = true;

        this.animationState.transition('updateData');
    }

    private formatLabel(value: AgNumericValue) {
        const { min, max } = this.properties.scale;
        return formatLabel(value, { min, max });
    }

    private layoutScale() {
        const { scale, properties } = this;
        const { seriesRectWidth, seriesRectHeight } = this.nodeDataDependencies;
        const { scale: scaleProps, outerRadius } = this.properties;
        const { min, max, label, interval } = scaleProps;

        const startAngle = toRadians(properties.startAngle - 90);
        const endAngle = toRadians(properties.endAngle - 90);

        const sweepAngle = normalizeAngle360Inclusive(endAngle - startAngle);
        const largerThanHalf = sweepAngle > Math.PI;
        const containsTop = largerThanHalf || isBetweenAngles(1.5 * Math.PI, startAngle, endAngle);
        const containsRight = largerThanHalf || isBetweenAngles(0 * Math.PI, startAngle, endAngle);
        const containsBottom = largerThanHalf || isBetweenAngles(0.5 * Math.PI, startAngle, endAngle);
        const containsLeft = largerThanHalf || isBetweenAngles(1 * Math.PI, startAngle, endAngle);

        let textAlign: TextAlign;
        if (containsLeft && !containsRight) {
            textAlign = 'right';
        } else if (!containsLeft && containsRight) {
            textAlign = 'left';
        } else {
            textAlign = 'center';
        }

        let verticalAlign: VerticalAlign;
        if (containsTop && !containsBottom) {
            verticalAlign = 'bottom';
        } else if (!containsTop && containsBottom) {
            verticalAlign = 'top';
        } else {
            verticalAlign = 'middle';
        }

        const unitBox = sectorBox({
            startAngle,
            endAngle,
            innerRadius: 0,
            outerRadius: 0.5,
        });
        const centerXOffset = -(unitBox.x + unitBox.width / 2) * 2;
        const centerYOffset = -(unitBox.y + unitBox.height / 2) * 2;
        const unitBoxSize = Math.min(seriesRectWidth / unitBox.width, seriesRectHeight / unitBox.height);

        scale.domain = [min, max];
        scale.range = [startAngle, endAngle];
        scale.arcLength = unitBoxSize / 2;

        const { maxSpacing, minSpacing } = interval;
        const { arcLength } = scale;
        const minTickCount = maxSpacing ? Math.floor(arcLength / maxSpacing) : 1;
        const maxTickCount = minSpacing ? Math.floor(arcLength / minSpacing) : Infinity;
        const preferredTickCount = Math.floor((4 / Math.PI) * Math.abs(scale.range[0] - scale.range[1]));
        const tickCount = Math.max(minTickCount, Math.min(maxTickCount, preferredTickCount));
        const ticks =
            interval.values ??
            // LinearAngleScale.ticks narrows to Number for its angular nice-stepping; for a bigint scale
            // domain generate full-precision bigint ticks directly so scale-label callbacks receive bigint.
            (typeof min === 'bigint' && typeof max === 'bigint' && interval.step == null
                ? createBigIntTicks(min, max, tickCount)
                : scale.ticks(
                      {
                          nice: [false, false],
                          interval: interval.step,
                          minTickCount,
                          maxTickCount,
                          tickCount,
                      },
                      [min, max]
                  )?.ticks) ??
            [];
        const tickFormatter = tickFormat(ticks, typeof label.format === 'string' ? label.format : undefined);

        const tickData: RadialGaugeTickDatum[] = [];

        for (const [index, value] of ticks.entries()) {
            let text: NormalisedTextOrSegments | undefined;
            if (label.formatter) {
                text = formatWithContext(this.ctx, label.formatter, {
                    type: 'number',
                    value,
                    index,
                    domain: [min, max],
                    boundSeries: undefined!,
                });
            }
            text ??= tickFormatter?.(value);

            if (text == null) continue;

            tickData.push({ index, value, text });
        }

        const baseRadius = 0.5 * unitBoxSize;
        const labelInset =
            label.enabled && outerRadius == null && tickData.length > 0
                ? this.getTickLabelInset({
                      tickData,
                      radius: baseRadius,
                      centerXOffset,
                      centerYOffset,
                      seriesRectWidth,
                      seriesRectHeight,
                      spacing: label.spacing,
                      rotation: toRadians(label.rotation ?? 0),
                  })
                : 0;

        const radiusBounds = Math.max(
            baseRadius - labelInset,
            // seriesRect may have negative size
            0
        );
        const radius = outerRadius ?? radiusBounds;

        this.centerX = seriesRectWidth / 2 + centerXOffset * radius;
        this.centerY = seriesRectHeight / 2 + centerYOffset * radius;
        this.radius = radius;
        this.textAlign = textAlign;
        this.verticalAlign = verticalAlign;

        return tickData;
    }

    protected getShapeFillBBox(): _ModuleSupport.ShapeFillBBox {
        const { centerX, centerY, radius } = this;
        const bbox = new BBox(centerX - radius, centerY - radius, 2 * radius, 2 * radius);

        return {
            series: bbox,
            axis: bbox,
        };
    }

    private getTargets(): Target[] {
        const { properties } = this;
        const defaultTarget = properties.defaultTarget;
        return properties.targets.map((target): Target => {
            const {
                text = defaultTarget.text,
                value = defaultTarget.value ?? 0,
                shape = defaultTarget.shape ?? 'triangle',
                rotation = defaultTarget.rotation ?? 0,
                placement = defaultTarget.placement ?? 'middle',
                spacing = defaultTarget.spacing ?? 0,
                size = defaultTarget.size ?? 0,
            } = target;
            const {
                enabled: labelEnabled = defaultTarget.label.enabled,
                color: labelColor = defaultTarget.label.color ?? 'black',
                fontStyle: labelFontStyle = defaultTarget.label.fontStyle ?? 'normal',
                fontWeight: labelFontWeight = defaultTarget.label.fontWeight ?? 'normal',
                fontSize: labelFontSize = defaultTarget.label.fontSize,
                fontFamily: labelFontFamily = defaultTarget.label.fontFamily,
                spacing: labelSpacing = defaultTarget.label.spacing ?? 0,
            } = target.label;

            return {
                text,
                value,
                shape,
                placement,
                spacing,
                size,
                rotation,
                label: {
                    enabled: labelEnabled,
                    color: labelColor,
                    fontStyle: labelFontStyle,
                    fontWeight: labelFontWeight,
                    fontSize: labelFontSize,
                    fontFamily: labelFontFamily,
                    spacing: labelSpacing,
                },
                style: target.getStyle(),
            };
        });
    }

    private getTargetRadius(target: Target) {
        const { radius, properties } = this;
        const { innerRadiusRatio, outerRadiusRatio } = properties;
        const { placement, spacing, size } = target;

        const outerRadius = radius * outerRadiusRatio;
        const innerRadius = radius * innerRadiusRatio;

        switch (placement) {
            case 'inside':
                return Math.max(innerRadius - spacing - size / 2, 0);
            case 'outside':
                return outerRadius + spacing + size / 2;
            default:
                return (innerRadius + outerRadius) / 2;
        }
    }

    private getTargetLabel(target: Target): RadialGaugeTargetDatumLabel {
        const { scale } = this;

        const { value, size, placement, label } = target;
        const { spacing, color: fill, fontStyle, fontWeight, fontSize, fontFamily } = label;
        const angle = scale.convert(value);

        const quadrant = Math.trunc(normalizeAngle360(angle) / (Math.PI / 2));

        const offset = size / 2 + spacing;

        let textAlign: CanvasTextAlign;
        let textBaseline: CanvasTextBaseline;
        let offsetX: number;
        let offsetY: number;
        switch (placement) {
            case 'outside':
                ({ textAlign, textBaseline } = outsideLabelPlacements[quadrant]);
                offsetX = offset * Math.cos(angle);
                offsetY = offset * Math.sin(angle);
                break;
            case 'inside':
                ({ textAlign, textBaseline } = insideLabelPlacements[quadrant]);
                offsetX = -offset * Math.cos(angle);
                offsetY = -offset * Math.sin(angle);
                break;
            default:
                textAlign = 'center';
                textBaseline = 'bottom';
                offsetX = 0;
                offsetY = -offset;
                break;
        }

        return {
            offsetX,
            offsetY,
            fill,
            textAlign,
            textBaseline,
            fontStyle,
            fontWeight,
            fontSize,
            fontFamily,
            lineHeight: undefined,
        };
    }

    override createNodeData() {
        const tickData = this.layoutScale();
        const { id: seriesId, scale, properties, radius, centerX, centerY } = this;

        const {
            value,
            innerRadiusRatio,
            outerRadiusRatio,
            segmentation,
            cornerRadius,
            cornerMode,
            needle,
            bar,
            scale: scaleProps,
            label,
            secondaryLabel,
        } = properties;
        const {
            outerRadius = radius * outerRadiusRatio,
            innerRadius = radius * innerRadiusRatio,
            defaultColorRange,
        } = properties;
        const targets = this.getTargets();

        const nodeData: RadialGaugeNodeDatum[] = [];
        const targetData: RadialGaugeTargetDatum[] = [];
        const needleData: RadialGaugeNeedleDatum[] = [];
        const labelData: RadialGaugeLabelDatum[] = [];
        const scaleData: RadialGaugeNodeDatum[] = [];

        const cornersOnAllItems = cornerMode === 'item';

        const containerStartAngle = scale.convert(scale.domain[0]);
        const containerEndAngle = scale.convert(value);

        const maxTicks = Math.ceil(normalizeAngle360Inclusive(containerEndAngle - containerStartAngle) * radius);
        let segments = segmentation.enabled ? segmentation.interval.getSegments(scale, maxTicks) : undefined;

        const barStyle = bar.getStyle(defaultColorRange, scale);
        const scaleStyle = scaleProps.getStyle(bar.enabled, defaultColorRange, scale);

        if (segments == null && cornersOnAllItems) {
            // convert() maps these whole-domain endpoints to the range ends, so a Number-narrow is precision-safe.
            const segmentStart = Number(scale.domainMin);
            const segmentEnd = Number(scale.domainMax);
            const datum = { value, segmentStart, segmentEnd };
            const appliedCornerRadius = Math.min(cornerRadius, (outerRadius - innerRadius) / 2);
            const angleInset = appliedCornerRadius / ((innerRadius + outerRadius) / 2);

            nodeData.push({
                series: this,
                itemId: `value`,
                datum,
                datumIndex: 0,
                type: NodeDataType.Node,
                centerX,
                centerY,
                outerRadius,
                innerRadius,
                startAngle: containerStartAngle - angleInset,
                endAngle: containerEndAngle + angleInset,
                clipStartAngle: undefined,
                clipEndAngle: undefined,
                startCornerRadius: cornerRadius,
                endCornerRadius: cornerRadius,
                style: barStyle,
            });

            scaleData.push({
                series: this,
                itemId: `scale`,
                datum,
                datumIndex: 0,
                type: NodeDataType.Node,
                centerX,
                centerY,
                outerRadius,
                innerRadius,
                startAngle: scale.range[0] - angleInset,
                endAngle: scale.range[1] + angleInset,
                clipStartAngle: undefined,
                clipEndAngle: undefined,
                startCornerRadius: cornerRadius,
                endCornerRadius: cornerRadius,
                style: scaleStyle,
            });
        } else {
            segments ??= scale.domain;

            for (let i = 0; i < segments.length - 1; i++) {
                const segmentStart = segments[i];
                const segmentEnd = segments[i + 1];
                const datum = { value, segmentStart, segmentEnd };

                const isStart = i === 0;
                const isEnd = i === segments.length - 2;

                const itemStartAngle = scale.convert(segmentStart);
                const itemEndAngle = scale.convert(segmentEnd);

                nodeData.push({
                    series: this,
                    itemId: `value-${i}`,
                    datum,
                    datumIndex: 0,
                    type: NodeDataType.Node,
                    centerX,
                    centerY,
                    outerRadius,
                    innerRadius,
                    startAngle: itemStartAngle,
                    endAngle: itemEndAngle,
                    clipStartAngle: containerStartAngle,
                    clipEndAngle: containerEndAngle,
                    startCornerRadius: cornersOnAllItems || isStart ? cornerRadius : 0,
                    endCornerRadius: cornersOnAllItems || isEnd ? cornerRadius : 0,
                    style: barStyle,
                });

                scaleData.push({
                    series: this,
                    itemId: `scale-${i}`,
                    datum,
                    datumIndex: 0,
                    type: NodeDataType.Node,
                    centerX,
                    centerY,
                    outerRadius,
                    innerRadius,
                    startAngle: itemStartAngle,
                    endAngle: itemEndAngle,
                    clipStartAngle: undefined,
                    clipEndAngle: undefined,
                    startCornerRadius: cornersOnAllItems || isStart ? cornerRadius : 0,
                    endCornerRadius: cornersOnAllItems || isEnd ? cornerRadius : 0,
                    style: scaleStyle,
                });
            }
        }

        if (!needle.enabled && label.enabled) {
            const {
                text,
                color: fill,
                fontSize,
                minimumFontSize,
                fontStyle,
                fontWeight,
                fontFamily,
                lineHeight,
                formatter = (params) => this.formatLabel(params.value),
            } = label;
            labelData.push({
                label: LabelType.Primary,
                centerX,
                centerY,
                text,
                value,
                fill,
                fontSize,
                minimumFontSize,
                fontStyle,
                fontWeight,
                fontFamily,
                lineHeight,
                formatter,
            });
        }

        if (!needle.enabled && secondaryLabel.enabled) {
            const {
                text,
                color: fill,
                fontSize,
                minimumFontSize,
                fontStyle,
                fontWeight,
                fontFamily,
                lineHeight,
                formatter,
            } = secondaryLabel;
            labelData.push({
                label: LabelType.Secondary,
                centerX,
                centerY,
                text,
                value,
                fill,
                fontSize,
                minimumFontSize,
                fontStyle,
                fontWeight,
                fontFamily,
                lineHeight,
                formatter,
            });
        }

        if (needle.enabled) {
            let needleRadius = needle.radiusRatio == null ? innerRadius : radius * needle.radiusRatio;
            needleRadius = Math.max(needleRadius - needle.spacing, 0);
            const needleAngle = scale.convert(value);

            needleData.push({
                centerX,
                centerY,
                radius: needleRadius,
                angle: needleAngle,
                series: this,
            });
        }

        for (let i = 0; i < targets.length; i += 1) {
            const target = targets[i];
            const { value: targetValue, text, size, shape, style } = target;

            // Exact bigint comparison: domainMin/Max flow the domain value type through unchanged.
            const { domainMin, domainMax } = scale;
            if (domainMin == null || domainMax == null || targetValue < domainMin || targetValue > domainMax) {
                continue;
            }

            const targetRadius = this.getTargetRadius(target);
            const targetAngle = scale.convert(targetValue);
            const targetRotation = toRadians(target.rotation + targetPlacementRotation[target.placement]);

            targetData.push({
                series: this,
                itemId: `target-${i}`,
                midPoint: {
                    x: targetRadius * Math.cos(targetAngle) + centerX,
                    y: targetRadius * Math.sin(targetAngle) + centerY,
                },
                datum: { value: targetValue },
                datumIndex: i + 1,
                type: NodeDataType.Target,
                value: targetValue,
                text,
                centerX,
                centerY,
                shape,
                radius: targetRadius,
                angle: targetAngle,
                rotation: targetRotation,
                size,
                label: this.getTargetLabel(target),
                style,
            });
        }

        return {
            itemId: seriesId,
            nodeData,
            needleData,
            targetData,
            labelData,
            scaleData,
            tickData,
        };
    }

    override findNodeDatum(itemId: AgActiveItemState['itemId']): SeriesNodeDatum | undefined {
        return findGaugeNodeDatum(this, itemId);
    }

    updateSelections(resize: boolean) {
        if (this.nodeDataRefresh || resize) {
            this.contextNodeData = this.createNodeData();
            this.nodeDataRefresh = false;
        }
    }

    private highlightDatum(node: _ModuleSupport.HighlightNodeDatum | undefined): RadialGaugeTargetDatum | undefined {
        if (node?.series === this && (node as RadialGaugeTargetDatum).type === NodeDataType.Target) {
            return node as RadialGaugeTargetDatum;
        }
    }

    override update({ seriesRect }: { seriesRect?: _ModuleSupport.BBox }) {
        const {
            datumSelection,
            labelSelection,
            needleSelection,
            targetSelection,
            targetLabelSelection,
            scaleSelection,
            highlightTargetSelection,
            tickSelection,
        } = this;

        const resize = this.checkResize(seriesRect);
        this.updateSelections(resize);

        this.contentGroup.visible = this.visible;
        this.contentGroup.opacity = this.getOpacity();

        const nodeData = this.contextNodeData?.nodeData ?? [];
        const labelData = this.contextNodeData?.labelData ?? [];
        const needleData = this.contextNodeData?.needleData ?? [];
        const targetData = this.contextNodeData?.targetData ?? [];
        const scaleData = this.contextNodeData?.scaleData ?? [];
        const tickData = this.contextNodeData?.tickData ?? [];

        const highlightTargetDatum = this.highlightDatum(this.ctx.highlightManager.getActiveHighlight());

        this.scaleSelection = this.updateScaleSelection({ scaleData, scaleSelection });
        this.updateScaleNodes({ scaleSelection });

        this.needleSelection = this.updateNeedleSelection({ needleData, needleSelection });
        this.updateNeedleNodes({ needleSelection });

        this.targetSelection = this.updateTargetSelection({ targetData, targetSelection });
        this.updateTargetStyles({ targetSelection, isHighlight: false });
        this.updateTargetNodes({ targetSelection });

        this.targetLabelSelection = this.updateTargetLabelSelection({ targetData, targetLabelSelection });
        this.updateTargetLabelNodes({ targetLabelSelection });

        this.datumSelection = this.updateDatumSelection({ nodeData, datumSelection });
        this.updateDatumNodes({ datumSelection });

        this.labelSelection = this.updateLabelSelection({ labelData, labelSelection });
        this.updateLabelNodes({ labelSelection });

        this.highlightTargetSelection = this.updateTargetSelection({
            targetData: highlightTargetDatum == null ? [] : [highlightTargetDatum],
            targetSelection: highlightTargetSelection,
        });
        this.updateTargetStyles({ targetSelection: highlightTargetSelection, isHighlight: true });
        this.updateTargetNodes({ targetSelection: highlightTargetSelection });

        this.tickSelection = this.updateTickSelection({ tickData, tickSelection });
        this.updateTickNodes({ tickSelection });

        if (resize) {
            this.animationState.transition('resize');
        }
        this.animationState.transition('update');
    }

    private updateDatumSelection(opts: {
        nodeData: RadialGaugeNodeDatum[];
        datumSelection: _ModuleSupport.Selection<RadialGaugeNodeDatum, _ModuleSupport.Sector<RadialGaugeNodeDatum>>;
    }) {
        return opts.datumSelection.update(opts.nodeData, undefined, (datum) => {
            return createDatumId(opts.nodeData.length, datum.itemId);
        });
    }

    private updateDatumNodes(opts: {
        datumSelection: _ModuleSupport.Selection<RadialGaugeNodeDatum, _ModuleSupport.Sector<RadialGaugeNodeDatum>>;
    }) {
        const { datumSelection } = opts;
        const { ctx, properties } = this;
        const { segmentation } = properties;
        const sectorSpacing = segmentation.spacing ?? 0;
        const animationDisabled = ctx.animationManager.isSkipped();

        const fillBBox = this.getShapeFillBBox();

        datumSelection.each((sector, datum) => {
            const { centerX, centerY, innerRadius, outerRadius, startCornerRadius, endCornerRadius } = datum;
            sector.centerX = centerX;
            sector.centerY = centerY;
            sector.innerRadius = innerRadius;
            sector.outerRadius = outerRadius;
            sector.pointerEvents = this.properties.bar.enabled
                ? _ModuleSupport.PointerEvents.All
                : _ModuleSupport.PointerEvents.None;

            sector.setStyleProperties(datum.style as NormalisedRadialGaugeSeriesStyle, fillBBox);

            sector.startOuterCornerRadius = startCornerRadius;
            sector.startInnerCornerRadius = startCornerRadius;
            sector.endOuterCornerRadius = endCornerRadius;
            sector.endInnerCornerRadius = endCornerRadius;

            sector.radialEdgeInset = (sectorSpacing + sector.strokeWidth) / 2;
            sector.concentricEdgeInset = sector.strokeWidth / 2;
            datum.midPoint = sector.getBBox().computeCenter();

            if (animationDisabled || sector.previousDatum == null) {
                sector.setProperties(resetRadialGaugeSeriesResetSectorFunction(sector, datum));
            }
        });

        this.datumUnion.update(datumSelection, this.itemGroup, _ModuleSupport.Sector, (node, first, last) => {
            node.clipSector ??= new SectorBox(Number.NaN, Number.NaN, Number.NaN, Number.NaN);
            node.centerX = first.centerX;
            node.centerY = first.centerY;
            node.outerRadius = node.clipSector.outerRadius = first.outerRadius;
            node.innerRadius = node.clipSector.innerRadius = first.innerRadius;
            node.startAngle = node.clipSector.startAngle = first.startAngle;
            node.startInnerCornerRadius = first.startInnerCornerRadius;
            node.startOuterCornerRadius = first.startOuterCornerRadius;
            node.endAngle = last.endAngle;
            node.clipSector.endAngle = last.clipSector?.endAngle ?? last.endAngle;
            node.endInnerCornerRadius = last.endInnerCornerRadius;
            node.endOuterCornerRadius = last.endOuterCornerRadius;
            node.pointerEvents = _ModuleSupport.PointerEvents.None;
        });
    }

    private updateScaleSelection(opts: {
        scaleData: RadialGaugeNodeDatum[];
        scaleSelection: _ModuleSupport.Selection<RadialGaugeNodeDatum, _ModuleSupport.Sector<RadialGaugeNodeDatum>>;
    }) {
        return opts.scaleSelection.update(opts.scaleData, undefined, (datum) => {
            return createDatumId(opts.scaleData.length, datum.itemId);
        });
    }

    private updateScaleNodes(opts: {
        scaleSelection: _ModuleSupport.Selection<RadialGaugeNodeDatum, _ModuleSupport.Sector<RadialGaugeNodeDatum>>;
    }) {
        const { scaleSelection } = opts;
        const { segmentation } = this.properties;
        const sectorSpacing = segmentation.spacing ?? 0;

        const fillBBox = this.getShapeFillBBox();

        scaleSelection.each((sector, datum) => {
            const { centerX, centerY, innerRadius, outerRadius, startCornerRadius, endCornerRadius } = datum;
            sector.centerX = centerX;
            sector.centerY = centerY;
            sector.innerRadius = innerRadius;
            sector.outerRadius = outerRadius;

            sector.setStyleProperties(datum.style as NormalisedRadialGaugeSeriesStyle, fillBBox);

            sector.startOuterCornerRadius = startCornerRadius;
            sector.startInnerCornerRadius = startCornerRadius;
            sector.endOuterCornerRadius = endCornerRadius;
            sector.endInnerCornerRadius = endCornerRadius;

            sector.radialEdgeInset = (sectorSpacing + sector.strokeWidth) / 2;
            sector.concentricEdgeInset = sector.strokeWidth / 2;

            sector.setProperties(resetRadialGaugeSeriesResetSectorFunction(sector, datum));
        });
    }

    private updateNeedleSelection(opts: {
        needleData: RadialGaugeNeedleDatum[];
        needleSelection: _ModuleSupport.Selection<RadialGaugeNeedleDatum, RadialGaugeNeedle>;
    }) {
        return opts.needleSelection.update(opts.needleData, undefined, () => createDatumId(0));
    }

    private updateNeedleNodes(opts: {
        needleSelection: _ModuleSupport.Selection<RadialGaugeNeedleDatum, RadialGaugeNeedle>;
    }) {
        const { needleSelection } = opts;
        const { fill, fillOpacity, stroke, strokeOpacity, strokeWidth, lineDash, lineDashOffset } =
            this.properties.needle;
        const animationDisabled = this.ctx.animationManager.isSkipped();

        needleSelection.each((needle, datum) => {
            const { centerX, centerY, radius } = datum;

            const scale = radius * 2;

            needle.d = RadialGaugeNeedle.defaultPathData;

            needle.setStyleProperties({
                fill,
                fillOpacity,
                stroke,
                strokeOpacity,
                strokeWidth: strokeWidth / scale,
                lineDash: lineDash.map((d) => d / scale),
                lineDashOffset: lineDashOffset / scale,
            });

            needle.translationX = centerX;
            needle.translationY = centerY;
            needle.scalingX = scale;
            needle.scalingY = scale;

            if (animationDisabled) {
                needle.setProperties(resetRadialGaugeSeriesResetNeedleFunction(needle, datum));
            }
        });
    }

    private updateTargetSelection(opts: {
        targetData: RadialGaugeTargetDatum[];
        targetSelection: _ModuleSupport.Selection<
            RadialGaugeTargetDatum,
            _ModuleSupport.Marker<RadialGaugeTargetDatum>
        >;
    }) {
        return opts.targetSelection.update(opts.targetData, undefined, (target) => target.itemId);
    }

    private updateTargetStyles({
        targetSelection,
        isHighlight,
    }: {
        targetSelection: _ModuleSupport.Selection<
            RadialGaugeTargetDatum,
            _ModuleSupport.Marker<RadialGaugeTargetDatum>
        >;
        isHighlight: boolean;
    }) {
        targetSelection.each((_, datum) => {
            datum.style = this.getTargetStyle(isHighlight, datum);
        });
    }

    private updateTargetNodes({
        targetSelection,
    }: {
        targetSelection: _ModuleSupport.Selection<
            RadialGaugeTargetDatum,
            _ModuleSupport.Marker<RadialGaugeTargetDatum>
        >;
    }) {
        targetSelection.each((target, datum) => {
            const { centerX, centerY, angle, radius, shape, size, rotation } = datum;

            target.setStyleProperties(datum.style as NormalisedRadialGaugeSeriesStyle);

            target.size = size;
            target.shape = shape === 'line' ? lineMarker : shape;
            target.translationX = centerX + radius * Math.cos(angle);
            target.translationY = centerY + radius * Math.sin(angle);
            target.rotation = angle + rotation;
        });
    }

    private getTargetStyle(isHighlight: boolean, { datumIndex, style }: RadialGaugeTargetDatum) {
        const highlightStyle = this.getHighlightStyle(isHighlight, datumIndex);
        const selectionStyle = this.getSelectionStyle(datumIndex);

        return mergeDefaults(selectionStyle, highlightStyle, {
            ...style,
            opacity: 1,
        });
    }

    private updateTargetLabelSelection(opts: {
        targetData: RadialGaugeTargetDatum[];
        targetLabelSelection: _ModuleSupport.Selection<
            RadialGaugeTargetDatum,
            _ModuleSupport.Text<RadialGaugeTargetDatum>
        >;
    }) {
        return opts.targetLabelSelection.update(opts.targetData, undefined, (target) => target.itemId);
    }

    private updateTargetLabelNodes(opts: {
        targetLabelSelection: _ModuleSupport.Selection<
            RadialGaugeTargetDatum,
            _ModuleSupport.Text<RadialGaugeTargetDatum>
        >;
    }) {
        const { targetLabelSelection } = opts;

        targetLabelSelection.each((label, target) => {
            const { centerX, centerY, radius, angle, text } = target;
            const { offsetX, offsetY, fill, fontStyle, fontWeight, fontSize, fontFamily, textAlign, textBaseline } =
                target.label;

            if (text == null) {
                label.visible = false;
                return;
            }

            label.visible = true;
            label.x = centerX + radius * Math.cos(angle) + offsetX;
            label.y = centerY + radius * Math.sin(angle) + offsetY;
            label.text = text;
            label.fill = fill;
            label.fontStyle = fontStyle;
            label.fontWeight = fontWeight;
            label.fontSize = fontSize;
            label.fontFamily = fontFamily;
            label.textAlign = textAlign;
            label.textBaseline = textBaseline;
        });
    }

    private updateLabelSelection(opts: {
        labelData: RadialGaugeLabelDatum[];
        labelSelection: _ModuleSupport.Selection<RadialGaugeLabelDatum, _ModuleSupport.Text<RadialGaugeLabelDatum>>;
    }) {
        return opts.labelSelection.update(opts.labelData, undefined, (datum) => datum.label);
    }

    private updateLabelNodes(opts: {
        labelSelection: _ModuleSupport.Selection<RadialGaugeLabelDatum, _ModuleSupport.Text<RadialGaugeLabelDatum>>;
    }) {
        const { labelSelection } = opts;
        const animationDisabled = this.ctx.animationManager.isSkipped();

        labelSelection.each((label, datum) => {
            label.fill = datum.fill;
            label.fontStyle = datum.fontStyle;
            label.fontWeight = datum.fontWeight;
            label.fontFamily = datum.fontFamily;
        });

        if (animationDisabled || this.labelsHaveExplicitText()) {
            this.formatLabelText();
        }
    }

    private updateTickSelection(opts: {
        tickData: RadialGaugeTickDatum[];
        tickSelection: _ModuleSupport.Selection<
            RadialGaugeTickDatum,
            _ModuleSupport.TransformableText<RadialGaugeTickDatum>
        >;
    }) {
        return opts.tickSelection.update(opts.tickData, undefined, (datum) => datum.index);
    }

    private updateTickNodes(opts: {
        tickSelection: _ModuleSupport.Selection<
            RadialGaugeTickDatum,
            _ModuleSupport.TransformableText<RadialGaugeTickDatum>
        >;
    }) {
        const { scale, radius, centerX, centerY, properties } = this;
        const { enabled, color, fontFamily, fontSize, fontStyle, fontWeight, spacing } = properties.scale.label;
        const rotation = toRadians(properties.scale.label.rotation ?? 0);

        opts.tickSelection.each((label, datum) => {
            if (!enabled) {
                label.visible = false;
                return;
            }

            label.visible = true;
            label.text = datum.text;
            label.fill = color;
            label.fontFamily = fontFamily;
            label.fontSize = fontSize;
            label.fontStyle = fontStyle;
            label.fontWeight = fontWeight;

            const angle = scale.convert(datum.value);
            const { textAlign, textBaseline } = this.getTickLabelAlign(angle);
            const x0 = centerX + (radius + spacing) * Math.cos(angle);
            const y0 = centerY + (radius + spacing) * Math.sin(angle);

            label.textAlign = textAlign;
            label.textBaseline = textBaseline;

            label.x = x0;
            label.y = y0;
            label.rotationCenterX = x0;
            label.rotationCenterY = y0;
            label.rotation = rotation;
        });
    }

    private getTickLabelAlign(tickAngle: number) {
        const cos = Math.cos(tickAngle);
        const sin = Math.sin(tickAngle);

        let textAlign: CanvasTextAlign;
        let textBaseline: CanvasTextBaseline;

        const isCos0 = isNumberEqual(cos, 0);
        const isSin0 = isNumberEqual(sin, 0);
        const isCosPositive = cos > 0 && !isCos0;
        const isSinPositive = sin > 0 && !isSin0;

        textAlign = 'right';
        if (isCos0) {
            textAlign = 'center';
        } else if (isCosPositive) {
            textAlign = 'left';
        }

        textBaseline = 'bottom';
        if (isSin0) {
            textBaseline = 'middle';
        } else if (isSinPositive) {
            textBaseline = 'top';
        }

        return { textAlign, textBaseline };
    }

    private getTickLabelInset(params: {
        tickData: RadialGaugeTickDatum[];
        radius: number;
        centerXOffset: number;
        centerYOffset: number;
        seriesRectWidth: number;
        seriesRectHeight: number;
        spacing: number;
        rotation: number;
    }) {
        const { tickData, radius, centerXOffset, centerYOffset, seriesRectWidth, seriesRectHeight, spacing, rotation } =
            params;
        const { label } = this.properties.scale;
        const centerX = seriesRectWidth / 2 + centerXOffset * radius;
        const centerY = seriesRectHeight / 2 + centerYOffset * radius;
        const tempText = new TransformableText();
        tempText.fontFamily = label.fontFamily;
        tempText.fontSize = label.fontSize;
        tempText.fontStyle = label.fontStyle;
        tempText.fontWeight = label.fontWeight;
        tempText.rotation = rotation;

        const minComponent = 1e-6;
        let inset = 0;
        for (const datum of tickData) {
            const angle = this.scale.convert(datum.value);
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const x = centerX + (radius + spacing) * cos;
            const y = centerY + (radius + spacing) * sin;
            const { textAlign, textBaseline } = this.getTickLabelAlign(angle);

            tempText.text = datum.text;
            tempText.x = x;
            tempText.y = y;
            tempText.textAlign = textAlign;
            tempText.textBaseline = textBaseline;
            tempText.rotationCenterX = x;
            tempText.rotationCenterY = y;

            const box = rotation ? Transformable.toCanvas(tempText) : tempText.getBBox();
            if (box == null) continue;

            const minX = box.x;
            const maxX = box.x + box.width;
            const minY = box.y;
            const maxY = box.y + box.height;

            const overflowLeft = Math.max(0, -minX);
            const overflowRight = Math.max(0, maxX - seriesRectWidth);
            const overflowTop = Math.max(0, -minY);
            const overflowBottom = Math.max(0, maxY - seriesRectHeight);

            const dxPerRadius = centerXOffset + cos;
            if (Math.abs(dxPerRadius) > minComponent) {
                if (overflowRight > 0 && dxPerRadius > 0) {
                    inset = Math.max(inset, overflowRight / dxPerRadius);
                } else if (overflowLeft > 0 && dxPerRadius < 0) {
                    inset = Math.max(inset, overflowLeft / -dxPerRadius);
                }
            }

            const dyPerRadius = centerYOffset + sin;
            if (Math.abs(dyPerRadius) > minComponent) {
                if (overflowBottom > 0 && dyPerRadius > 0) {
                    inset = Math.max(inset, overflowBottom / dyPerRadius);
                } else if (overflowTop > 0 && dyPerRadius < 0) {
                    inset = Math.max(inset, overflowTop / -dyPerRadius);
                }
            }
        }

        return inset;
    }

    labelsHaveExplicitText() {
        for (const { datum } of this.labelSelection) {
            if (datum.text == null) {
                return false;
            }
        }

        return true;
    }

    formatLabelText(datum?: { label: AgNumericValue | undefined; secondaryLabel: AgNumericValue | undefined }) {
        const { labelSelection, radius, textAlign, verticalAlign } = this;
        const { spacing: padding, innerRadiusRatio } = this.properties;

        formatRadialGaugeLabels(
            this,
            this.ctx,
            labelSelection,
            { padding, textAlign, verticalAlign },
            radius * innerRadiusRatio,
            datum
        );
    }

    protected resetAllAnimation() {
        this.ctx.animationManager.stopByAnimationGroupId(this.id);

        resetMotion([this.datumSelection], resetRadialGaugeSeriesResetSectorFunction);
        resetMotion([this.needleSelection], resetRadialGaugeSeriesResetNeedleFunction);
        this.formatLabelText();
    }

    resetAnimation(phase: ChartAnimationPhase) {
        if (phase === 'initial') {
            this.animationState.transition('reset');
        } else if (phase === 'ready') {
            this.animationState.transition('skip');
        }
    }

    private animateLabelText(params: { from?: AgNumericValue; phase?: _ModuleSupport.AnimationPhase } = {}) {
        const { animationManager } = this.ctx;

        let labelFrom: AgNumericValue | undefined;
        let labelTo: AgNumericValue | undefined;
        let secondaryLabelFrom: AgNumericValue | undefined;
        let secondaryLabelTo: AgNumericValue | undefined;
        this.labelSelection.each((label, datum) => {
            // Reset animation
            label.opacity = 1;

            if (datum.label === LabelType.Primary) {
                labelFrom = label.previousDatum?.value ?? params.from ?? datum.value;
                labelTo = datum.value;
            } else if (datum.label === LabelType.Secondary) {
                secondaryLabelFrom = label.previousDatum?.value ?? params.from ?? datum.value;
                secondaryLabelTo = datum.value;
            }
        });

        if (this.labelsHaveExplicitText()) {
            // Ignore
        } else if (labelTo == null || secondaryLabelTo == null) {
            this.formatLabelText();
        } else if (Number(labelFrom) === Number(labelTo) && Number(secondaryLabelFrom) === Number(secondaryLabelTo)) {
            // Number() compare so a value type change (`5n === 5` is false) doesn't force a no-op animation.
            this.formatLabelText({ label: labelTo, secondaryLabel: secondaryLabelTo });
        } else {
            const animationId = `${this.id}_labels`;

            // The count-up tween narrows to Number; onStop re-formats from the full-precision label values.
            animationManager.animate({
                id: animationId,
                groupId: 'label',
                from: { label: Number(labelFrom), secondaryLabel: Number(secondaryLabelFrom) },
                to: { label: Number(labelTo), secondaryLabel: Number(secondaryLabelTo) },
                phase: params.phase ?? 'update',
                onUpdate: (datum) => this.formatLabelText(datum),
                onStop: () => this.formatLabelText({ label: labelTo, secondaryLabel: secondaryLabelTo }),
            });
        }
    }

    animateEmptyUpdateReady() {
        const { animationManager } = this.ctx;

        const { node, needle } = prepareRadialGaugeSeriesAnimationFunctions(true, this.scale.range[0]);
        fromToMotion(this.id, 'node', animationManager, [this.datumSelection], node, (_sector, datum) => datum.itemId);
        fromToMotion(this.id, 'needle', animationManager, [this.needleSelection], needle, () => 'needle');

        fromToMotion(
            this.id,
            'label',
            animationManager,
            [this.labelSelection],
            fadeInFns,
            (label) => label.unsafeDatum.label
        );

        this.animateLabelText({
            from: this.properties.scale.min,
            phase: 'initial',
        });
    }

    animateWaitingUpdateReady() {
        const { animationManager } = this.ctx;

        const { node, needle } = prepareRadialGaugeSeriesAnimationFunctions(false, this.scale.range[0]);
        fromToMotion(this.id, 'node', animationManager, [this.datumSelection], node, (_sector, datum) => datum.itemId);
        fromToMotion(this.id, 'needle', animationManager, [this.needleSelection], needle, () => 'needle');

        this.animateLabelText();
    }

    protected animateReadyResize() {
        this.resetAllAnimation();
    }

    override dataCount(): number {
        return Number.NaN; // Not used
    }

    override getSeriesDomain() {
        return { domain: [Number.NaN, Number.NaN] };
    }

    override getSeriesRange(): [number, number] {
        return [Number.NaN, Number.NaN];
    }

    override getLegendData(): _ModuleSupport.ChartLegendDatum<any>[] {
        return [];
    }

    override getTooltipContent(datumIndex: _ModuleSupport.DatumIndex): _ModuleSupport.TooltipContent | undefined {
        const { id: seriesId, properties } = this;
        const { tooltip } = properties;

        const { value, text, fallbackLabel } = getGaugeTooltipInfo(this, datumIndex);
        if (value == null) return;

        return this.formatTooltipWithContext(
            tooltip,
            {
                data: [{ label: text, fallbackLabel, value: this.formatLabel(value) }],
            },
            { seriesId, title: undefined, datum: undefined, value }
        );
    }

    override pickNodeClosestDatum(point: Point): _ModuleSupport.SeriesNodePickMatch | undefined {
        return pickGaugeNearestDatum(this, point);
    }

    override pickFocus(opts: _ModuleSupport.PickFocusInputs): _ModuleSupport.PickFocusOutputs | undefined {
        return pickGaugeFocus(this, opts);
    }

    getCaptionText(): string {
        const { value } = this.properties;

        const description: string[] = [];

        description.push(this.formatLabel(value));

        this.labelSelection.each((_label, datum) => {
            const text = getLabelText(this.id, this.ctx, datum);
            if (text != null) {
                description.push(toPlainText(text));
            }
        });

        return description.join('. ');
    }

    getCategoryValue(_datumIndex: _ModuleSupport.DatumIndex) {
        return;
    }

    datumIndexForCategoryValue(_categoryValue: any): _ModuleSupport.DatumIndex | undefined {
        return;
    }

    protected override hasItemStylers(): boolean {
        return this.properties.selection.enabled || this.properties.label.itemStyler != null;
    }
}
